/**
 * 问卷收集管理（07 功能 PRD §3.3，核心新增）：
 * 列表只显元数据（时间/类型/状态/称呼摘要）→ 详情（结构化问答按剧本字段顺序 +
 * 附件下载 + 同意记录）；详情查看与附件下载后端写审计（前端无感）。
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import type {
  AdminSubmissionDetail,
  AdminSubmissionListResponse,
  ConversationStep,
} from '@chronospeak/contracts';
import { conversationScripts, paperShortLabels } from '@chronospeak/contracts';
import { adminHttp, ApiError } from '../lib/api';
import { Button } from '@chronospeak/ui';
import { ErrorNote, Loading, PageHeader, TableWrap } from '../components/ui-bits';

const STATUS_LABEL: Record<string, string> = {
  PENDING: '待处理',
  STORED: '已入库',
  REJECTED: '已拒绝',
};

/* ---------------- 列表（只显元数据） ---------------- */

export function SubmissionsListPage() {
  const [data, setData] = useState<AdminSubmissionListResponse | null>(null);
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await adminHttp.get<AdminSubmissionListResponse>(
        `/admin/contact-submissions?pageSize=50${type ? `&type=${type}` : ''}${status ? `&status=${status}` : ''}`,
      );
      setData(res);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '加载失败');
    }
  }, [type, status]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section>
      <PageHeader
        title='问卷收集'
        sub='列表只显元数据；完整个人信息进入详情才展开（全程审计）。'
        actions={
          <>
            <label style={{ fontSize: 13, color: 'var(--muted)' }}>
              类型{' '}
              <select value={type} onChange={(e) => setType(e.target.value)} className='mgr-select'>
                <option value=''>全部</option>
                <option value='creators'>创作者</option>
                <option value='partners'>合作伙伴</option>
              </select>
            </label>
            <label style={{ fontSize: 13, color: 'var(--muted)' }}>
              状态{' '}
              <select value={status} onChange={(e) => setStatus(e.target.value)} className='mgr-select'>
                <option value=''>全部</option>
                {Object.entries(STATUS_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
          </>
        }
      />
      {error && <ErrorNote message={error} />}
      {!data && !error && <Loading />}
      {data && (
        <>
          <TableWrap>
            <thead>
              <tr>
                <th scope='col'>提交时间</th>
                <th scope='col'>类型</th>
                <th scope='col'>状态</th>
                <th scope='col'>称呼</th>
                <th scope='col'>公司</th>
                <th scope='col'>语言</th>
                <th scope='col'>附件</th>
                <th scope='col' />
              </tr>
            </thead>
            <tbody>
              {data.items.map((s) => (
                <tr key={s.id}>
                  <td>{new Date(s.createdAt).toLocaleString('zh-CN')}</td>
                  <td>{s.type === 'creators' ? '创作者' : '合作伙伴'}</td>
                  <td>{STATUS_LABEL[s.status] ?? s.status}</td>
                  <td>{s.nameSummary || '—'}</td>
                  <td>{s.companySummary || '—'}</td>
                  <td>{s.locale}</td>
                  <td>{s.assetCount > 0 ? `📎 ${s.assetCount}` : '—'}</td>
                  <td>
                    <Button variant='ghost' size='sm' onClick={() => navigate(`/submissions/${s.id}`)}>
                      查看详情
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          <p style={{ fontSize: 12, color: 'var(--muted)' }}>
            共 {data.total} 条 · 无邮件通知（决策 1），请定期查看。
          </p>
        </>
      )}
    </section>
  );
}

/* ---------------- 详情（结构化问答 + 附件 + 同意记录） ---------------- */

/** 按剧本字段顺序渲染问答；跳过显示（略） */
function AnswersList({ detail }: { detail: AdminSubmissionDetail }) {
  const script = conversationScripts[detail.type];
  const labels = paperShortLabels[detail.type];
  return (
    <dl className='mgr-answers'>
      {script.steps.map((step: ConversationStep) => {
        const value = detail.payload[step.key] ?? '';
        const assetKey = detail.assetKeys?.[step.key];
        const skipped = !value && !assetKey;
        return (
          <div key={step.key} className='mgr-answer-row'>
            <dt>
              {labels[step.key]?.zh ?? step.key}
              <span style={{ color: 'var(--muted)', fontWeight: 400 }}>
                {' '}
                · {step.type}
                {step.required ? ' · 必答' : ''}
              </span>
            </dt>
            <dd>
              {skipped ? (
                <span style={{ color: 'var(--muted)' }}>（略）</span>
              ) : (
                <>
                  {value && <span style={{ whiteSpace: 'pre-wrap' }}>{value}</span>}
                  {assetKey && (
                    <DownloadAssetButton submissionId={detail.id} assetKey={assetKey} />
                  )}
                </>
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

function DownloadAssetButton({ submissionId, assetKey }: { submissionId: string; assetKey: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await adminHttp.get<{ url: string }>(
        `/admin/contact-submissions/${submissionId}/assets/${encodeURIComponent(assetKey)}/download-url`,
      );
      // 浏览器下载（新窗口触发；URL 短期有效）
      window.open(res.url, '_blank', 'noopener');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '签发下载链接失败');
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className='mgr-asset'>
      <Button variant='ghost' size='sm' onClick={() => void download()} disabled={busy}>
        📎 下载附件
      </Button>
      {error && <ErrorNote message={error} />}
    </span>
  );
}

export function SubmissionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<AdminSubmissionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminHttp
      .get<AdminSubmissionDetail>(`/admin/contact-submissions/${id}`)
      .then(setDetail)
      .catch((e) => setError(e instanceof ApiError ? e.message : '加载失败'));
  }, [id]);

  if (error && !detail) {
    return (
      <section>
        <PageHeader title='问卷详情' />
        <ErrorNote message={error} />
      </section>
    );
  }
  if (!detail) {
    return (
      <section>
        <PageHeader title='问卷详情' />
        <Loading />
      </section>
    );
  }

  return (
    <section>
      <PageHeader
        title={`问卷详情 · ${detail.type === 'creators' ? '创作者' : '合作伙伴'}`}
        sub={`提交于 ${new Date(detail.createdAt).toLocaleString('zh-CN')} · 状态 ${
          STATUS_LABEL[detail.status] ?? detail.status
        } · 来源语言 ${detail.locale}`}
        actions={<Button variant='ghost' size='sm' onClick={() => navigate('/submissions')}>返回列表</Button>}
      />
      <p className='mgr-consent'>
        隐私同意：版本 <code>{detail.consentVersion}</code> · 同意时间{' '}
        {new Date(detail.consentAt).toLocaleString('zh-CN')}
        <span style={{ color: 'var(--muted)' }}>（本次查看已记入审计日志）</span>
      </p>
      <AnswersList detail={detail} />
    </section>
  );
}
